// Libs
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import validator from "validator";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// Models
import { MoneyGifterModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/MoneyGifterModel";
import { MoneyGifterDTO } from "Globals/Models/DTO/MoneyGifterDTO";

// ViewModels
import IncomeDetailsViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsViewModel";

export default class MoneyGifterViewModel extends ViewModelBase<MoneyGifterModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(moneyGifterModel?: MoneyGifterModel) {
        super(moneyGifterModel ? moneyGifterModel : new MoneyGifterModel());
    }

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<MoneyGifterModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public stash = async (incomeDetails: IncomeDetailsViewModel): Promise<void> => {
        let valid = await this.isModelValid();

        if (valid) {
            let model = new MoneyGifterModel();
            model.fromDto(this.model);
            let viewModel = new MoneyGifterViewModel(model);

            runInAction(() => {
                incomeDetails.moneyGifterViewModels.push(viewModel);
                // triggers the auto-save.
                incomeDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (incomeDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.incomeDetailId = incomeDetailId;

            let dto: MoneyGifterDTO = this.model.toDto();
            const apiResult = await this.Post<MoneyGifterDTO>(AppUrls.Server.ApplicationForm.Upsert.MoneyGifter, dto);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("MoneyGifter:Upsert-Successful");
                return Promise.resolve();
            } else {
                console.error("MoneyGifter:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (incomeDetails: IncomeDetailsViewModel, isDeleted: boolean = false): Promise<void> => {
        try {
            if (!this.model.getValue("incomeDetailId")) {
                runInAction(() => {
                    let index = incomeDetails.moneyGifterViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        incomeDetails.moneyGifterViewModels.splice(index, 1);
                    }
                });
            } else {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                this.model.incomeDetailId = incomeDetails.model.id ? incomeDetails.model.id : "";
                this.model.isDeleted = isDeleted;

                let upsertMoneyGifter: MoneyGifterDTO = this.model.toDto();
                const apiResult = await this.Post<MoneyGifterDTO>(AppUrls.Server.ApplicationForm.Upsert.MoneyGifter, upsertMoneyGifter);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("MoneyGifter:Upsert-Successful");

                    let model = new MoneyGifterModel();
                    model.fromDto(apiResult.payload);
                    let viewModel = new MoneyGifterViewModel(model);
                    const filtered = incomeDetails.moneyGifterViewModels.filter((a) => a.model.isDeleted == false);
                    runInAction(() => {
                        if (isDeleted) {
                            incomeDetails.moneyGifterViewModels = filtered;
                        } else {
                            incomeDetails.moneyGifterViewModels.push(viewModel);
                        }
                    });
                    this.model.resetModel();
                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("MoneyGifter:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public handleFieldChange = (fieldName: keyof FieldType<MoneyGifterModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @computed
    public get isModelDirty(): boolean {
        let dirty = false;
        Object.keys(this.model).forEach((field) => {
            if (this.model.getDirty(field as keyof FieldType<MoneyGifterModel>)) {
                dirty = true;
            }
        });
        return dirty;
    }
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<MoneyGifterModel>, value);
        });
    }

    public async isFieldValid(fieldName: keyof FieldType<MoneyGifterModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
